import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  packageLabelIn,
  type PackageLabelsType,
  type PackageRoleWordsType,
} from '@featit/shared/package-text';
import { useLocale } from '@/i18n/LocaleProvider';
import { fetchPackageLabels, fetchPackageRoleWords } from '@/services/PackagesApi';
import { usePageWorkAreaId } from '@/routing/pageWorkAreaContext';

/**
 * I-1977: a package's names — page and block titles, state, role and type names, field labels and
 * options — in the reader's language. The server stores each as its English words and answers the
 * other languages' words from them (`GET packages/labels`); a screen shows `name(text)`.
 *
 * English is what is stored, so an English reader asks nothing, and a name the package gives no
 * German, or any name a person wrote here, reads as it is.
 */
export type PackageNameFn = ((text: string) => string) & {
  /**
   * I-2088: a German reader's names are still on their way. A screen that draws a name waits with a
   * skeleton while this is true, and never draws the English it is stored in first.
   */
  readonly loading?: boolean;
};

/** One answer per work area, kept for the session; `forgetPackageNames` drops it after an install. */
const answers = new Map<string, PackageLabelsType>();
const pending = new Map<string, Promise<PackageLabelsType>>();

function loadPackageLabels(workAreaId: string): Promise<PackageLabelsType> {
  const known = answers.get(workAreaId);
  if (known) return Promise.resolve(known);
  let load = pending.get(workAreaId);
  if (!load) {
    load = fetchPackageLabels(workAreaId)
      .then((labels) => {
        answers.set(workAreaId, labels);
        return labels;
      })
      .finally(() => pending.delete(workAreaId));
    pending.set(workAreaId, load);
  }
  return load;
}

/** I-2496: the role words of each work area, kept for the session like its labels. */
const roleWordAnswers = new Map<string, PackageRoleWordsType>();
const roleWordsPending = new Map<string, Promise<PackageRoleWordsType>>();

function loadPackageRoleWords(workAreaId: string): Promise<PackageRoleWordsType> {
  const known = roleWordAnswers.get(workAreaId);
  if (known) return Promise.resolve(known);
  let load = roleWordsPending.get(workAreaId);
  if (!load) {
    // A read that cannot even be asked (an older server's client) fails like a refused one.
    load = Promise.resolve()
      .then(() => fetchPackageRoleWords(workAreaId))
      .then((words) => {
        roleWordAnswers.set(workAreaId, words);
        return words;
      })
      .finally(() => roleWordsPending.delete(workAreaId));
    roleWordsPending.set(workAreaId, load);
  }
  return load;
}

/** After an install or upgrade here, the names are read again. */
export function forgetPackageNames(workAreaId: string): void {
  answers.delete(workAreaId);
  pending.delete(workAreaId);
  roleWordAnswers.delete(workAreaId);
  roleWordsPending.delete(workAreaId);
}

/**
 * I-2496 (AGM-77): the word the packages installed in `workAreaId` give each role, by role key, in
 * every language (`GET packages/role-words`), or null while it is on its way or when it cannot be read.
 * Read in English too: the package's word is the role's name in every language. `skip` reads nothing,
 * for a screen that already holds the words (the access overview's `roles[].packageName`).
 */
export function usePackageRoleWordsOf(
  workAreaId: string | null | undefined,
  skip = false,
): { words: PackageRoleWordsType | null; loading: boolean } {
  const wanted = Boolean(workAreaId) && !skip;
  const [words, setWords] = useState<PackageRoleWordsType | null>(() =>
    wanted && workAreaId ? (roleWordAnswers.get(workAreaId) ?? null) : null,
  );
  const [unreadFor, setUnreadFor] = useState<string | null>(null);
  useEffect(() => {
    if (!wanted || !workAreaId) {
      setWords(null);
      return;
    }
    let live = true;
    setWords(roleWordAnswers.get(workAreaId) ?? null);
    loadPackageRoleWords(workAreaId).then(
      (loaded) => {
        if (live) setWords(loaded);
      },
      // Unread, a role keeps the name it has without a package's word: nothing waits.
      () => {
        if (live) setUnreadFor(workAreaId);
      },
    );
    return () => {
      live = false;
    };
  }, [wanted, workAreaId]);
  return { words, loading: wanted && words === null && unreadFor !== workAreaId };
}

/** The names of the packages installed in `workAreaId`, in the reader's locale. */
export function usePackageNamesOf(workAreaId: string | null | undefined): PackageNameFn {
  const locale = useLocale();
  const wanted = Boolean(workAreaId) && locale !== 'en';
  const [labels, setLabels] = useState<PackageLabelsType | null>(() =>
    wanted && workAreaId ? (answers.get(workAreaId) ?? null) : null,
  );
  // The work area whose read failed: its names keep the English they are stored in, and nothing waits.
  const [unreadFor, setUnreadFor] = useState<string | null>(null);
  useEffect(() => {
    if (!wanted || !workAreaId) {
      setLabels(null);
      return;
    }
    let live = true;
    setLabels(answers.get(workAreaId) ?? null);
    loadPackageLabels(workAreaId).then(
      (loaded) => {
        if (live) setLabels(loaded);
      },
      // Unread, a name keeps the English it is stored in: the page still reads, and says nothing wrong.
      () => {
        if (live) setUnreadFor(workAreaId);
      },
    );
    return () => {
      live = false;
    };
  }, [wanted, workAreaId]);
  const loading = wanted && labels === null && unreadFor !== workAreaId;
  return useMemo(
    () =>
      Object.assign((text: string) => (labels ? packageLabelIn(text, labels, locale) : text), {
        loading,
      }),
    [labels, locale, loading],
  );
}

/**
 * The names of the packages installed in several work areas at once, for a screen that lists what
 * spans them (a block's item-type filter across work areas, I-2088): a name reads in the first work
 * area's words that give it any. `loading` until every area has answered or failed.
 */
export function usePackageNamesOfAreas(workAreaIds: readonly string[]): PackageNameFn {
  const locale = useLocale();
  const key = [...new Set(workAreaIds)].sort().join('|');
  const wanted = key !== '' && locale !== 'en';
  const [settled, setSettled] = useState<{ key: string; labels: PackageLabelsType[] } | null>(null);
  useEffect(() => {
    if (!wanted) {
      setSettled(null);
      return;
    }
    let live = true;
    void Promise.allSettled(key.split('|').map((id) => loadPackageLabels(id))).then((results) => {
      if (!live) return;
      setSettled({
        key,
        labels: results.flatMap((result) => (result.status === 'fulfilled' ? [result.value] : [])),
      });
    });
    return () => {
      live = false;
    };
  }, [wanted, key]);
  const loading = wanted && settled?.key !== key;
  return useMemo(
    () =>
      Object.assign(
        (text: string) => {
          for (const labels of settled?.key === key ? settled.labels : []) {
            const named = packageLabelIn(text, labels, locale);
            if (named !== text) return named;
          }
          return text;
        },
        { loading },
      ),
    [settled, key, locale, loading],
  );
}

const PackageNamesContext = createContext<PackageNameFn | null>(null);

/** Gives everything under it these names, for a screen that reads them itself too. */
export const PackageNamesScope = PackageNamesContext.Provider;

/** Gives everything under it the names of `workAreaId`'s packages in the reader's locale. */
export function PackageNamesProvider({
  workAreaId,
  children,
}: {
  workAreaId: string | null | undefined;
  children: ReactNode;
}) {
  const name = usePackageNamesOf(workAreaId);
  return <PackageNamesContext.Provider value={name}>{children}</PackageNamesContext.Provider>;
}

/**
 * The package names of the work area this screen is in: a `PackageNamesProvider`'s, else the work area
 * of the page on screen (`PageWorkAreaContext`, I-2485); as they are outside both.
 */
export function usePackageName(): PackageNameFn {
  const scoped = useContext(PackageNamesContext);
  const pageWorkAreaId = usePageWorkAreaId();
  const ofPage = usePackageNamesOf(scoped ? null : pageWorkAreaId);
  return scoped ?? ofPage;
}
